import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Autocomplete,
  TextField,
  CircularProgress,
  Chip,
  Grid,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Stream as StreamIcon,
  Factory,
  CheckCircle,
  Warning,
  Settings,
  Dock,
  CalendarToday,
  DataUsage,
  Refresh,
  RestartAlt,
  Send,
} from '@mui/icons-material';
import { Title, useGetList } from 'react-admin';
import {
  useGetApiDiagrams,
  useGetApiDiagramsId,
  useGetApiPlantStatusPlantId,
  usePutApiPlantStatusPlantIdDataRange,
  usePutApiPlantStatusPlantIdScadaChanged,
} from '@/api/gen/api';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { PlantStatusDto } from '@/types';
import { DiagramDto } from '@/api/gen/model';

// Valores observados en el JSON
export type PermissionLevel = 'READ' | 'ADMIN';

export interface RolePermission {
  role: string;
  permissionLevel: PermissionLevel;
}

export interface DiagramItem {
  id: number;
  name: string;
  description: string | null;
  plantId: number;
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
  nodes: null; // según el JSON actual
  edges: null; // según el JSON actual
  rolePermissions: RolePermission[];
  permissionLevel: PermissionLevel;
}

export interface DiagramsResponse {
  totalCount: number;
  page: number;
  pageSize: number;
  items: DiagramItem[];
}

export const DataStream: React.FC = () => {
  const [selectedPlantId, setSelectedPlantId] = useState<number | null>(null);
  const [openDateDialog, setOpenDateDialog] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const {
    data: plantStatusRaw,
    isLoading: isLoadingPlantStatus,
    refetch: refetchPlantStatus,
  } = useGetApiPlantStatusPlantId(selectedPlantId);

  const { mutate: resetPlant, isLoading: isResetting } =
    usePutApiPlantStatusPlantIdScadaChanged();

  const { mutate: updateDataRange, isLoading: isUpdatingDataRange } =
    usePutApiPlantStatusPlantIdDataRange();

  // Type assertion para el plantStatus
  const plantStatus = plantStatusRaw as unknown as PlantStatusDto | undefined;

  // Usar useGetList para obtener las plantas desde el dataProvider
  const { data: plants, isLoading } = useGetList('plants', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'plantName', order: 'ASC' },
  });

  // Función para formatear fechas
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No disponible';
    try {
      return format(new Date(dateString), "dd/MM/yyyy 'a las' HH:mm:ss", {
        locale: es,
      });
    } catch {
      return 'Fecha inválida';
    }
  };

  const handleSendDataRange = () => {
    if (!selectedPlantId || !startDate || !endDate) return;

    // Convertir fechas locales a UTC para .NET
    const startDateUTC = new Date(startDate).toISOString();
    const endDateUTC = new Date(endDate).toISOString();

    updateDataRange(
      {
        plantId: selectedPlantId,
        data: {
          rangeStartDate: startDateUTC,
          rangeEndDate: endDateUTC,
          shouldSendData: true,
        },
      },
      {
        onSuccess: () => {
          console.log('Rango de datos actualizado exitosamente');
          setOpenDateDialog(false);
          setStartDate('');
          setEndDate('');
          refetchPlantStatus();
        },
        onError: error => {
          console.error('Error al actualizar rango de datos:', error);
        },
      }
    );
  };

  // Auto-refresh cada 2 segundos cuando hay una planta seleccionada
  useEffect(() => {
    if (!selectedPlantId) return;

    const interval = setInterval(() => {
      refetchPlantStatus();
    }, 2000);

    return () => clearInterval(interval);
  }, [selectedPlantId, refetchPlantStatus]);

  return (
    <Box sx={{ p: 3 }}>
      <Title title='DataStream' />

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 3,
          p: 2,
          borderRadius: 1,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
        }}
      >
        <StreamIcon sx={{ fontSize: 40 }} />
        <Box>
          <Typography variant='h5' sx={{ fontWeight: 'bold' }}>
            DataStream
          </Typography>
          <Typography variant='body2' sx={{ opacity: 0.9 }}>
            Gestión de flujos de datos en tiempo real
          </Typography>
        </Box>
      </Box>

      {/* Selector de Planta */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Factory color='primary' />
              <Typography variant='h6'>Seleccionar Planta</Typography>
            </Box>
            {selectedPlantId && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant='outlined'
                  startIcon={<Refresh />}
                  onClick={() => refetchPlantStatus()}
                  disabled={isLoadingPlantStatus}
                >
                  Actualizar
                </Button>
                <Button
                  variant='contained'
                  color='warning'
                  startIcon={<RestartAlt />}
                  onClick={() => {
                    if (selectedPlantId) {
                      resetPlant(
                        { plantId: selectedPlantId, params: { value: true } },
                        {
                          onSuccess: () => {
                            console.log('Planta reiniciada exitosamente');
                            refetchPlantStatus();
                          },
                          onError: error => {
                            console.error('Error al reiniciar planta:', error);
                          },
                        }
                      );
                    }
                  }}
                  disabled={isResetting || isLoadingPlantStatus}
                >
                  {isResetting ? 'Reiniciando...' : 'Reiniciar Planta'}
                </Button>
              </Box>
            )}
          </Box>

          <Autocomplete
            options={plants || []}
            getOptionLabel={option => option.plantName || `Planta ${option.id}`}
            loading={isLoading}
            onChange={(event, newValue) => {
              const plantId = newValue?.id || null;
              setSelectedPlantId(plantId);
              console.log('Planta seleccionada ID:', plantId);
            }}
            renderInput={params => (
              <TextField
                {...params}
                label='Seleccione una planta'
                variant='outlined'
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? (
                        <CircularProgress color='inherit' size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            fullWidth
          />
        </CardContent>
      </Card>

      {/* Estado de la Planta */}
      {selectedPlantId && !isLoadingPlantStatus && plantStatus && (
        <Grid container spacing={3}>
          {/* Apartado 1: Cambios SCADA sin aplicar */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                height: '100%',
                borderLeft: plantStatus.hasScadaChanges ? 4 : 0,
                borderColor: plantStatus.hasScadaChanges
                  ? 'warning.main'
                  : 'transparent',
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <Settings
                    sx={{
                      fontSize: 32,
                      color: plantStatus.hasScadaChanges
                        ? 'warning.main'
                        : 'success.main',
                    }}
                  />
                  <Typography variant='h6'>Cambios SCADA</Typography>
                </Box>

                <Chip
                  icon={
                    plantStatus.hasScadaChanges ? <Warning /> : <CheckCircle />
                  }
                  label={
                    plantStatus.hasScadaChanges
                      ? 'Cambios Pendientes'
                      : 'Sin Cambios'
                  }
                  color={plantStatus.hasScadaChanges ? 'warning' : 'success'}
                  sx={{ mb: 2 }}
                />

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarToday
                    sx={{ fontSize: 18, color: 'text.secondary' }}
                  />
                  <Typography variant='caption' color='text.secondary'>
                    Última modificación:
                  </Typography>
                </Box>
                <Typography variant='body2' sx={{ mt: 0.5, fontWeight: 500 }}>
                  {formatDate(plantStatus.lastScadaChangeDate)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Apartado 2: Cambios Docker */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                height: '100%',
                borderLeft: plantStatus.requiresDockerUpdate ? 4 : 0,
                borderColor: plantStatus.requiresDockerUpdate
                  ? 'error.main'
                  : 'transparent',
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <Dock
                    sx={{
                      fontSize: 32,
                      color: plantStatus.requiresDockerUpdate
                        ? 'error.main'
                        : 'success.main',
                    }}
                  />
                  <Typography variant='h6'>Docker Update</Typography>
                </Box>

                <Chip
                  icon={
                    plantStatus.requiresDockerUpdate ? (
                      <Warning />
                    ) : (
                      <CheckCircle />
                    )
                  }
                  label={
                    plantStatus.requiresDockerUpdate
                      ? 'Actualización Requerida'
                      : 'Actualizado'
                  }
                  color={plantStatus.requiresDockerUpdate ? 'error' : 'success'}
                  sx={{ mb: 2 }}
                />

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarToday
                    sx={{ fontSize: 18, color: 'text.secondary' }}
                  />
                  <Typography variant='caption' color='text.secondary'>
                    Última solicitud:
                  </Typography>
                </Box>
                <Typography variant='body2' sx={{ mt: 0.5, fontWeight: 500 }}>
                  {formatDate(plantStatus.lastDockerUpdateRequestDate)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Apartado 3: Rango de Datos */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                height: '100%',
                borderLeft: plantStatus.shouldSendData ? 4 : 0,
                borderColor: plantStatus.shouldSendData
                  ? 'info.main'
                  : 'transparent',
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <DataUsage
                    sx={{
                      fontSize: 32,
                      color: plantStatus.shouldSendData
                        ? 'info.main'
                        : 'text.secondary',
                    }}
                  />
                  <Typography variant='h6'>Envío de Datos</Typography>
                </Box>

                <Chip
                  icon={
                    plantStatus.shouldSendData ? <Warning /> : <CheckCircle />
                  }
                  label={
                    plantStatus.shouldSendData ? 'Envío Pendiente' : 'Al Día'
                  }
                  color={plantStatus.shouldSendData ? 'info' : 'success'}
                  sx={{ mb: 2 }}
                />

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <Typography variant='caption' color='text.secondary'>
                    Inicio del rango:
                  </Typography>
                  <Typography variant='body2' sx={{ fontWeight: 500 }}>
                    {formatDate(plantStatus.rangeStartDate)}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant='caption' color='text.secondary'>
                    Fin del rango:
                  </Typography>
                  <Typography variant='body2' sx={{ fontWeight: 500 }}>
                    {formatDate(plantStatus.rangeEndDate)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarToday
                    sx={{ fontSize: 18, color: 'text.secondary' }}
                  />
                  <Typography variant='caption' color='text.secondary'>
                    Última actualización:
                  </Typography>
                </Box>
                <Typography variant='body2' sx={{ mt: 0.5, fontWeight: 500 }}>
                  {formatDate(plantStatus.lastDataRangeUpdateDate)}
                </Typography>

                <Button
                  variant='contained'
                  color='info'
                  startIcon={<Send />}
                  fullWidth
                  sx={{ mt: 2 }}
                  onClick={() => setOpenDateDialog(true)}
                >
                  Configurar Envío
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Dialog para configurar rango de fechas */}
      <Dialog
        open={openDateDialog}
        onClose={() => setOpenDateDialog(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Configurar Rango de Datos</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label='Fecha de Inicio'
              type='datetime-local'
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label='Fecha de Fin'
              type='datetime-local'
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDateDialog(false)}>Cancelar</Button>
          <Button
            variant='contained'
            onClick={handleSendDataRange}
            disabled={!startDate || !endDate || isUpdatingDataRange}
          >
            {isUpdatingDataRange ? 'Enviando...' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading State */}
      {selectedPlantId && isLoadingPlantStatus && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* No hay planta seleccionada */}
      {!selectedPlantId && (
        <Card>
          <CardContent>
            <Typography variant='body1' color='text.secondary' align='center'>
              Seleccione una planta para ver su estado
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
